import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import {
  Button, Container, Badge, Modal, Form,
  Spinner, Row, Col, Tab, Nav
} from 'react-bootstrap';
import {
  FaEdit, FaPowerOff, FaChartBar, FaGift, FaStar, FaBolt,
  FaGlobe, FaPlus, FaTrash, FaSave, FaSync
} from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';
import './ManageAgencySubscriptionplans.css';

const PLAN_TYPE_CONFIG = {
  free: { icon: <FaGift />, color: '#6c757d', gradient: 'linear-gradient(135deg, #6c757d, #868e96)', label: 'Free' },
  pay_per_click: { icon: <FaBolt />, color: '#0d6efd', gradient: 'linear-gradient(135deg, #0d6efd, #6495ed)', label: 'Pay Per Use' },
  silver: { icon: <FaStar />, color: '#8E9AAF', gradient: 'linear-gradient(135deg, #8E9AAF, #5C6B7A)', label: 'Silver' },
  gold: { icon: <FaStar />, color: '#f4a903', gradient: 'linear-gradient(135deg, #f4a903, #ffc107)', label: 'Gold' }
};

const FEATURE_ICONS = {
  candidate_post: <FaPlus />,
  candidate_posting: <FaPlus />,
  job_post: <FaPlus />,
  job_posting: <FaPlus />,
  agency_repost_access: <FaSync />
};

export default function ManageAgencySubscriptionplans() {
  const [plans, setPlans] = useState([]);
  const [featuresMaster, setFeaturesMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posting');

  // Edit plan modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', validity_days: 30, features: [] });
  const [saving, setSaving] = useState(false);

  // Country pricing modal
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingPlanId, setPricingPlanId] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({ country_code: '', currency: '', base_price: 0, is_active: true });

  const navigate = useNavigate();

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BaseUrl}/api/admin/subscription-plans`);
      setPlans(res.data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'Failed to load plans', 'error');
    }
    setLoading(false);
  }, []);

  const fetchFeaturesMaster = useCallback(async () => {
    try {
      const res = await axios.get(`${BaseUrl}/api/admin/features-master`);
      setFeaturesMaster(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchPlans(); fetchFeaturesMaster(); }, [fetchPlans, fetchFeaturesMaster]);

  // Group plans by category
  const postingPlans = plans.filter(p => p.category === 'posting');
  const contactPlans = plans.filter(p => p.category === 'contact');

  // ─── Edit Plan ─────────────────────────────────────────────
  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setEditForm({
      name: plan.name,
      validity_days: plan.validity_days,
      features: (plan.all_features || plan.features || []).map(f => ({
        id: f.id,
        feature_id: f.feature_id,
        feature_key: f.feature_key,
        feature_label: f.feature_label,
        is_enabled: !!f.is_enabled,
        usage_limit: f.usage_limit,
        price_per_unit: f.price_per_unit
      }))
    });
    setShowEditModal(true);
  };

  const saveEditPlan = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Update plan settings
      await axios.put(`${BaseUrl}/api/admin/subscription-plans/${editingPlan.id}`, {
        name: editForm.name,
        validity_days: editForm.validity_days,
        is_active: editingPlan.is_active
      });

      // Bulk update features
      if (editForm.features.length > 0) {
        await axios.put(`${BaseUrl}/api/admin/plan-features/bulk-update`, {
          features: editForm.features.map(f => ({
            id: f.id,
            is_enabled: f.is_enabled,
            usage_limit: f.usage_limit,
            price_per_unit: f.price_per_unit
          }))
        });
      }

      setShowEditModal(false);
      fetchPlans();
      Swal.fire({ icon: 'success', title: 'Saved!', text: 'Plan updated successfully.', timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'Failed to save', 'error');
    }
    setSaving(false);
  };

  const updateFeatureField = (idx, field, value) => {
    setEditForm(prev => {
      const features = [...prev.features];
      features[idx] = { ...features[idx], [field]: value };
      return { ...prev, features };
    });
  };

  // ─── Toggle Plan ───────────────────────────────────────────
  const togglePlan = async (plan) => {
    try {
      const res = await axios.put(`${BaseUrl}/api/admin/subscription-plans/${plan.id}/toggle`);
      fetchPlans();
      Swal.fire({ icon: 'success', title: 'Updated', text: `Plan is now ${res.data.is_active ? 'Active' : 'Inactive'}`, timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'Failed', 'error');
    }
  };

  // ─── Country Pricing ───────────────────────────────────────
  const openAddPricing = (planId) => {
    setPricingPlanId(planId);
    setEditingPricing(null);
    setPricingForm({ country_code: '', currency: '', base_price: 0, is_active: true });
    setShowPricingModal(true);
  };

  const openEditPricing = (planId, pricing) => {
    setPricingPlanId(planId);
    setEditingPricing(pricing);
    setPricingForm({
      country_code: pricing.country_code,
      currency: pricing.currency,
      base_price: pricing.base_price,
      is_active: pricing.is_active
    });
    setShowPricingModal(true);
  };

  const savePricing = async (e) => {
    e.preventDefault();
    try {
      if (editingPricing) {
        await axios.put(`${BaseUrl}/api/admin/plan-country-pricing/${editingPricing.id}`, pricingForm);
      } else {
        await axios.post(`${BaseUrl}/api/admin/plan-country-pricing`, { plan_id: pricingPlanId, ...pricingForm });
      }
      setShowPricingModal(false);
      fetchPlans();
      Swal.fire({ icon: 'success', title: 'Saved!', timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'Failed', 'error');
    }
  };

  const deletePricing = async (pricingId) => {
    const r = await Swal.fire({ title: 'Remove pricing?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33' });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${BaseUrl}/api/admin/plan-country-pricing/${pricingId}`);
      fetchPlans();
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'Failed', 'error');
    }
  };

  // ─── Plan Card Render ──────────────────────────────────────
  const renderPlanCard = (plan) => {
    const config = PLAN_TYPE_CONFIG[plan.type] || PLAN_TYPE_CONFIG.free;
    const visibleFeatures = (plan.features || []).filter(f => {
      if (plan.category === 'posting') {
        const key = (f.feature_key || f.feature_label || '').toLowerCase();
        return !key.includes('contact');
      }
      return true;
    });
    return (
      <Col key={plan.id} xs={12} md={6} lg={3}>
        <div className={`masp-plan-card ${!plan.is_active ? 'masp-plan-inactive' : ''}`}>
          {/* Card Header */}
          <div className="masp-plan-header" style={{ background: config.gradient }}>
            <div className="masp-plan-icon">{config.icon}</div>
            <h4 className="masp-plan-name">{plan.name}</h4>
            <Badge className="masp-plan-type-badge">{config.label}</Badge>
            <div className="masp-plan-validity">{plan.validity_days} days validity</div>
          </div>

          {/* Card Body */}
          <div className="masp-plan-body">
            {/* Status */}
            <div className="masp-plan-status-row">
              <span className="masp-label">Status</span>
              <Badge bg={plan.is_active ? 'success' : 'danger'} className="masp-status-badge">
                {plan.is_active ? '● Active' : '● Inactive'}
              </Badge>
            </div>

            {/* Features */}
            <div className="masp-section-title">Features & Limits</div>
            {visibleFeatures.length > 0 ? (
              <div className="masp-features-list">
                {visibleFeatures.map(f => (
                  <div key={f.id} className="masp-feature-row">
                    <div className="d-flex align-items-center flex-1">
                      <span className="me-2 text-primary" style={{ fontSize: '0.9rem' }}>
                        {FEATURE_ICONS[f.feature_key] || <FaPlus />}
                      </span>
                      <span className="masp-feature-label">{f.feature_label.replace(/\s*\((Employer|Candidate|Agency|Job\s*Seeker)\)/gi, '')}</span>
                    </div>
                    <div className="masp-feature-values">
                      <span className="masp-feature-limit">
                        {f.usage_limit === 0 ? '∞' : f.usage_limit} uses
                      </span>
                      {plan.type === 'pay_per_click' && (
                        <span className="masp-feature-price">{Number(f.price_per_unit)}/use</span>
                      )}
                      <Badge bg={f.is_enabled ? 'success' : 'secondary'} className="masp-feature-badge">
                        {f.is_enabled ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="masp-no-data">No features configured</p>
            )}

            {/* Country Pricing */}
            <div className="masp-section-title">
              <span>Country Pricing</span>
              <Button size="sm" variant="link" className="masp-add-pricing-btn p-0" onClick={() => openAddPricing(plan.id)}>
                <FaPlus /> Add
              </Button>
            </div>
            {plan.pricing?.length > 0 ? (
              <div className="masp-pricing-list">
                {plan.pricing.map(p => (
                  <div key={p.id} className="masp-pricing-row">
                    <div className="masp-pricing-info">
                      <FaGlobe className="masp-pricing-icon" />
                      <strong>{p.country_code}</strong>
                      <span className="masp-pricing-amount">{p.currency} {Number(p.base_price).toLocaleString()}</span>
                    </div>
                    <div className="masp-pricing-actions">
                      <button className="masp-icon-btn masp-icon-btn-edit" onClick={() => openEditPricing(plan.id, p)} title="Edit"><FaEdit /></button>
                      <button className="masp-icon-btn masp-icon-btn-delete" onClick={() => deletePricing(p.id)} title="Delete"><FaTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="masp-no-data">No pricing set</p>
            )}
          </div>

          {/* Card Footer */}
          <div className="masp-plan-footer">
            <Button
              size="sm"
              className="masp-btn-edit"
              onClick={() => openEditPlan(plan)}
            >
              <FaEdit /> Edit Plan
            </Button>
            <Button
              size="sm"
              className={`masp-btn-toggle ${plan.is_active ? 'masp-btn-deactivate' : 'masp-btn-activate'}`}
              onClick={() => togglePlan(plan)}
            >
              <FaPowerOff /> {plan.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
        </div>
      </Col>
    );
  };

  // ─── Main Render ───────────────────────────────────────────
  return (
    <>
      <AdminNavbar />
      <Container className="masp-container">
        {/* Page Header */}
        <div className="masp-header">
          <div>
            <h2 className="masp-title">Agency Subscription Plans</h2>
            <p className="masp-subtitle">Manage plans, features, limits, and pricing for agencies</p>
          </div>
          <Button variant="outline-primary" className="masp-analytics-btn" onClick={() => navigate('/admin-subscription-analytics')}>
            <FaChartBar /> Analytics
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : (
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            {/* Category Tabs */}
            <Nav variant="pills" className="masp-tabs mb-4">
              <Nav.Item>
                <Nav.Link eventKey="posting" className="masp-tab-link">
                  📋 Job & Candidate Posting Subscription
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="contact" className="masp-tab-link">
                  📞 Candidate Contact Subscription
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="posting">
                <Row className="g-4">
                  {postingPlans.length > 0 ? (
                    [...postingPlans]
                    .sort((a, b) => {
                      const order = { free: 0, pay_per_click: 1, silver: 2, gold: 3 };
                      return (order[a.type] ?? 99) - (order[b.type] ?? 99);
                    })
                    .map(plan => renderPlanCard(plan))
                  ) : (
                    <Col><p className="text-center text-muted py-5">No posting plans found. Please run the database migration to seed plans.</p></Col>
                  )}
                </Row>
              </Tab.Pane>
              <Tab.Pane eventKey="contact">
                <Row className="g-4">
                  {contactPlans.length > 0 ? (
                    [...contactPlans]
                    .sort((a, b) => {
                      const order = { free: 0, pay_per_click: 1, silver: 2, gold: 3 };
                      return (order[a.type] ?? 99) - (order[b.type] ?? 99);
                    })
                    .map(plan => renderPlanCard(plan))
                  ) : (
                    <Col><p className="text-center text-muted py-5">No contact plans found. Please run the database migration to seed plans.</p></Col>
                  )}
                </Row>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        )}

        {/* ────────── Edit Plan Modal ────────── */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" className="masp-modal">
          <Form onSubmit={saveEditPlan}>
            <Modal.Header closeButton className="masp-modal-header">
              <Modal.Title>
                <FaEdit className="me-2" />
                Edit Plan — {editingPlan?.name}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="masp-modal-body">
              {/* Plan Settings */}
              <div className="masp-modal-section">
                <h6 className="masp-modal-section-title">Plan Settings</h6>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="masp-form-label">Plan Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={editForm.name}
                        onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        required
                        className="masp-form-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="masp-form-label">Validity (Days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={editForm.validity_days}
                        onChange={e => setEditForm(f => ({ ...f, validity_days: parseInt(e.target.value) || 30 }))}
                        required
                        className="masp-form-input"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Features Config */}
              {editForm.features.length > 0 && (
                <div className="masp-modal-section">
                  <h6 className="masp-modal-section-title">Feature Configuration</h6>
                  {editForm.features.map((f, idx) => (
                    <div key={f.id} className="masp-feature-edit-row">
                      <div className="masp-feature-edit-name d-flex align-items-center">
                        <span className="me-2 text-primary" style={{ fontSize: '1rem' }}>
                          {FEATURE_ICONS[f.feature_key] || <FaPlus />}
                        </span>
                        <Form.Check
                          type="switch"
                          checked={f.is_enabled}
                          onChange={e => updateFeatureField(idx, 'is_enabled', e.target.checked)}
                          label={f.feature_label}
                          className="masp-feature-switch"
                        />
                      </div>
                      <div className="masp-feature-edit-fields">
                        <Form.Group className="masp-feature-field">
                          <Form.Label className="masp-form-label-sm">Limit <small>(0=∞)</small></Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={f.usage_limit}
                            onChange={e => updateFeatureField(idx, 'usage_limit', parseInt(e.target.value) || 0)}
                            className="masp-form-input-sm"
                          />
                        </Form.Group>
                        {editingPlan?.type === 'pay_per_click' && (
                          <Form.Group className="masp-feature-field">
                            <Form.Label className="masp-form-label-sm">Price/Unit</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={f.price_per_unit}
                              onChange={e => updateFeatureField(idx, 'price_per_unit', parseFloat(e.target.value) || 0)}
                              className="masp-form-input-sm"
                            />
                          </Form.Group>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="masp-modal-footer">
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" className="masp-btn-save" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : <FaSave className="me-2" />}
                Save Changes
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* ────────── Pricing Modal ────────── */}
        <Modal show={showPricingModal} onHide={() => setShowPricingModal(false)} centered className="masp-modal">
          <Form onSubmit={savePricing}>
            <Modal.Header closeButton className="masp-modal-header">
              <Modal.Title><FaGlobe className="me-2" />{editingPricing ? 'Edit' : 'Add'} Country Pricing</Modal.Title>
            </Modal.Header>
            <Modal.Body className="masp-modal-body">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="masp-form-label">Country Code</Form.Label>
                    <Form.Control
                      type="text" maxLength={5} placeholder="AE"
                      value={pricingForm.country_code}
                      onChange={e => setPricingForm(p => ({ ...p, country_code: e.target.value.toUpperCase() }))}
                      required className="masp-form-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="masp-form-label">Currency</Form.Label>
                    <Form.Control
                      type="text" maxLength={5} placeholder="AED"
                      value={pricingForm.currency}
                      onChange={e => setPricingForm(p => ({ ...p, currency: e.target.value.toUpperCase() }))}
                      required className="masp-form-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="masp-form-label">Base Price</Form.Label>
                    <Form.Control
                      type="number" min="0" step="0.01"
                      value={pricingForm.base_price}
                      onChange={e => setPricingForm(p => ({ ...p, base_price: parseFloat(e.target.value) || 0 }))}
                      required className="masp-form-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Form.Check
                    type="switch"
                    label="Active"
                    checked={!!pricingForm.is_active}
                    onChange={e => setPricingForm(p => ({ ...p, is_active: e.target.checked }))}
                    className="mb-2"
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="masp-modal-footer">
              <Button variant="secondary" onClick={() => setShowPricingModal(false)}>Cancel</Button>
              <Button type="submit" className="masp-btn-save"><FaSave className="me-2" />Save Pricing</Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
}
